import { motion } from "framer-motion";
import { Clock, Compass, HandHeart, MapPin, Palette, Sparkles, Users } from "lucide-react";
import { rhythmAccentStyle, ordinalWord, locTimes, timeRange, DEFAULT_START } from "@/lib/fridays";

const THEME_ICON = { 1: Palette, 2: HandHeart, 3: Users, 4: Compass };
const THEME_SLUG = { 1: "arts", 2: "generosity", 3: "youth", 4: "intentionality" };

export default function ThemeCard({ loc, isNext = false, index = 0 }) {
  const Icon = THEME_ICON[loc.friday_ordinal] || Sparkles;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1], delay: index * 0.06 }}
      style={rhythmAccentStyle(loc.friday_ordinal)}
      className="h-full"
    >
      <article
        className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border bg-card transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_24px_60px_rgba(92,52,18,0.12)] ${isNext ? "border-transparent ring-2 ring-primary" : "border-border"}`}
        data-testid={`theme-card-${THEME_SLUG[loc.friday_ordinal] || loc.friday_ordinal}`}
      >
        {loc.theme_art && (
          <div className="relative h-36 overflow-hidden" data-testid="theme-art-image">
            <img src={loc.theme_art} alt="" aria-hidden="true" loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-card/10 to-card" />
          </div>
        )}
        <div className="relative flex flex-1 flex-col p-6">
        {!loc.theme_art && (
          <div
            className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-[0.16] blur-2xl transition-opacity duration-300 group-hover:opacity-30"
            style={{ backgroundColor: "var(--fc-friday-accent)" }}
          />
        )}
        <div className="flex items-start justify-between gap-3">
          <span
            className="flex h-12 w-12 items-center justify-center rounded-2xl"
            style={{ backgroundColor: "color-mix(in oklab, var(--fc-friday-accent) 14%, transparent)", color: "var(--fc-friday-accent)" }}
          >
            <Icon className="h-5.5 w-5.5" />
          </span>
          <span className="rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-widest" style={{ color: "var(--fc-friday-accent)", borderColor: "color-mix(in oklab, var(--fc-friday-accent) 40%, transparent)" }}>
            {ordinalWord(loc.friday_ordinal)} Friday{isNext ? " · next" : ""}
          </span>
        </div>
        <h3 className="mt-5 font-display text-2xl font-semibold leading-tight text-foreground">{loc.theme_title}</h3>
        {loc.theme_tagline && <p className="mt-1.5 text-sm font-medium" style={{ color: "var(--fc-friday-accent)" }}>{loc.theme_tagline}</p>}
        {loc.theme_description && <p className="mt-3 text-sm leading-6 text-muted-foreground">{loc.theme_description}</p>}
        <div className="mt-auto space-y-1.5 border-t border-border pt-4 text-sm text-muted-foreground">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="theme-card-hours">
            <Clock className="h-4 w-4 shrink-0" />
            <span>Fridays · <span className="font-medium text-foreground">{timeRange(locTimes(loc).start, locTimes(loc).end)}</span></span>
            {locTimes(loc).start !== DEFAULT_START && <span className="inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold text-white" style={{ backgroundColor: "var(--fc-friday-accent)" }}>later start</span>}
          </p>
          <p className="flex items-center gap-2">
            <MapPin className="h-4 w-4 shrink-0" />
            <span><span className="font-medium text-foreground">{loc.name}</span>{loc.room ? ` · ${loc.room}` : ""}</span>
          </p>
        </div>
        </div>
      </article>
    </motion.div>
  );
}
